/* ---------------------------------------------------------------------------
   Flyouts: the desk's menus.

   A button that says `data-menu="account"`, and under it — while `State.menu`
   names it — a panel of commands. One open at a time, closed by choosing
   something, by Escape, by pressing anywhere else, or by the window changing
   size. The state is the store's like every other thing on screen, so a repaint
   redraws an open menu exactly as it was.

   FIXED, NOT ABSOLUTE. The buttons that open these sit inside boxes that clip —
   the ribbon's command band scrolls sideways, a panel hides what overflows its
   corners — so the flyout is placed against the viewport, from the button's own
   box, measured at the moment it is drawn. The button is already on screen when
   it is pressed, so there is always a box to measure.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS, type Child, type IconPath } from '../core/dom.ts';
import { actions, store, type MenuId, type State } from '../core/store.ts';

/** What a button that opens a flyout carries. */
export function menuButton(s: Readonly<State>, id: MenuId): Record<string, unknown> {
  return {
    'data-menu': id,
    'aria-haspopup': 'menu',
    'aria-expanded': s.menu === id ? 'true' : 'false',
    onclick: () => actions.toggleMenu(id),
  };
}

/** The gap between a button and the flyout under it. */
const GAP = 4;
/** How close to the window's edge a flyout may come. */
const MARGIN = 8;

/**
 * The flyout for `id`, placed under its button — or null when it is shut.
 *
 * `align: 'end'` lines its right edge up with the button's, for the buttons at
 * the right of the title bar and the tab row; `'start'` its left edge.
 */
export function flyout(
  s: Readonly<State>,
  id: MenuId,
  opts: { label: string; width: number; align?: 'start' | 'end' },
  ...children: Child[]
): HTMLElement | null {
  if (s.menu !== id || typeof document === 'undefined') return null;
  const anchor = document.querySelector<HTMLElement>(`[data-menu="${id}"]`);
  const box = anchor?.getBoundingClientRect();
  const top = box ? box.bottom + GAP : 48;
  const vw = typeof window === 'undefined' ? 1440 : window.innerWidth;
  const left = box
    ? opts.align === 'end'
      ? Math.max(MARGIN, box.right - opts.width)
      : Math.min(box.left, vw - opts.width - MARGIN)
    : MARGIN;

  return h(
    'div',
    {
      class: 'flyout',
      role: 'menu',
      'aria-label': opts.label,
      style: { top: `${top}px`, left: `${left}px`, width: `${opts.width}px` },
    },
    ...children,
  );
}

/* ------------------------------ the parts ------------------------------ */

/**
 * One command. Choosing it closes the menu first, so an action that opens a
 * dialog or changes app does not have a menu left hanging over the result.
 */
export function menuItem(opts: {
  label: string;
  onClick: () => void;
  iconPath?: IconPath;
  /** A second, smaller line: what it means rather than what it is called. */
  sub?: string;
  /** For one of a set: the one in force carries a tick. */
  checked?: boolean;
  /** The count or key printed at the right. */
  aside?: string;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}): HTMLElement {
  return h(
    'button',
    {
      type: 'button',
      class: ['flyout__item', opts.danger && 'flyout__item--danger'],
      role: opts.checked === undefined ? 'menuitem' : 'menuitemradio',
      'aria-checked': opts.checked === undefined ? undefined : String(opts.checked),
      disabled: opts.disabled || undefined,
      title: opts.title,
      onclick: () => {
        actions.closeMenu();
        opts.onClick();
      },
    },
    opts.iconPath
      ? icon(opts.iconPath, { size: 15 })
      : h('span', { class: 'flyout__noicon', 'aria-hidden': 'true' }),
    h(
      'span',
      { class: 'flyout__text' },
      opts.label,
      opts.sub ? h('span', { class: 'flyout__sub', text: opts.sub }) : null,
    ),
    opts.aside ? h('span', { class: 'flyout__aside', text: opts.aside }) : null,
    opts.checked ? h('span', { class: 'flyout__tick' }, icon(ICONS.check, { size: 14, width: 2 })) : null,
  );
}

export function menuLabel(text: string): HTMLElement {
  return h('div', { class: 'flyout__label', role: 'presentation', text });
}

export function menuSep(): HTMLElement {
  return h('div', { class: 'flyout__sep', role: 'separator' });
}

/** A block of non-command content — a header, a row of facts, a footer. */
export function menuSection(...children: Child[]): HTMLElement {
  return h('div', { class: 'flyout__section' }, ...children);
}

/* ------------------------------ dismissal ------------------------------ */

let installed = false;

/**
 * Close the open flyout on Escape, on a press outside it and its button, and
 * when the window resizes (the flyout was placed for the old size). Once, at
 * boot, from src/main.ts.
 */
export function installFlyouts(): void {
  if (installed || typeof document === 'undefined') return;
  installed = true;

  document.addEventListener(
    'pointerdown',
    (e) => {
      const open = store.get().menu;
      if (open === null) return;
      const target = e.target as Element | null;
      /* Inside the flyout is a choice, handled by the item. On its own button
         is the toggle, handled by the button — closing here as well would close
         and reopen it on the same press. */
      if (target?.closest('.flyout') || target?.closest(`[data-menu="${open}"]`)) return;
      actions.closeMenu();
    },
    true,
  );

  document.addEventListener('keydown', (e) => {
    const open = store.get().menu;
    if (open === null || e.key !== 'Escape') return;
    e.preventDefault();
    actions.closeMenu();
    document.querySelector<HTMLElement>(`[data-menu="${open}"]`)?.focus();
  });

  window.addEventListener('resize', () => actions.closeMenu());
}
